import { baseUrl } from './Constants'
import setCredentialsHeader from './setCredentialsHeader'

/**
 * Lightweight product-usage tracking. Events are queued and sent in batches
 * to POST /usage/events (requires a logged-in session; failures are ignored).
 */
const FLUSH_INTERVAL_MS = 5000
const FLUSH_SIZE = 20
const MAX_QUEUE = 200

let queue = []
let timer = null
let enabled = false

/** Sending is enabled only for a logged-in user (the endpoint needs a session). */
export const setUsageEnabled = (value) => {
  enabled = !!value
  if (!enabled) queue = []
}

const send = (events, useBeacon) => {
  const body = JSON.stringify({ events })
  const url = `${baseUrl}/usage/events`
  if (useBeacon && navigator.sendBeacon) {
    try {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))
      return
    } catch (e) { /* fall through to fetch */ }
  }
  fetch(url, {
    method: 'POST',
    credentials: setCredentialsHeader(),
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true
  }).catch(() => {})
}

export const flushUsage = (useBeacon = false) => {
  if (timer) { clearTimeout(timer); timer = null }
  if (!enabled || !queue.length) return
  const batch = queue
  queue = []
  send(batch, useBeacon)
}

export const trackEvent = (event, meta) => {
  if (!enabled || !event) return
  queue.push({ event, path: window.location.pathname, meta, ts: new Date().toISOString() })
  if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE)
  if (queue.length >= FLUSH_SIZE) {
    flushUsage()
  } else if (!timer) {
    timer = setTimeout(() => flushUsage(), FLUSH_INTERVAL_MS)
  }
}

export const trackPageView = (pathname) => {
  if (!enabled) return
  queue.push({ event: 'page_view', path: pathname, ts: new Date().toISOString() })
  if (!timer) timer = setTimeout(() => flushUsage(), FLUSH_INTERVAL_MS)
}

const errorMeta = (message, stack, extra) => ({
  message: String(message || '').slice(0, 300),
  stack: stack ? String(stack).slice(0, 800) : undefined,
  ...extra
})

const isExtensionError = (stack, file) =>
  /(chrome|moz|safari)-extension:\/\//.test(String(stack || '') + String(file || ''))

/** Records an unexpected error (uncaught exception, rejection or React crash). */
export const trackError = (event, message, stack, extra) => {
  // Errors thrown by the user's browser extensions are not ours
  if (isExtensionError(stack, extra && extra.file)) return
  trackEvent(event, errorMeta(message, stack, extra))
}

// Flush what is left when the tab is closed or hidden; capture uncaught errors
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
    trackError('ui.error', e.message, e.error && e.error.stack, { file: e.filename, line: e.lineno })
  })
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason
    trackError('ui.error', reason && reason.message ? reason.message : reason, reason && reason.stack, { kind: 'rejection' })
  })
  window.addEventListener('pagehide', () => flushUsage(true))
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushUsage(true)
  })
}
